import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trophy,
  DollarSign,
  Wallet,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader2,
  Users,
  Coins,
  Info,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * JoinTournamentButton - Button to join a tournament with auto-payment
 * Now supports both Golf Wallet (USD) and Golf Coins
 */
const JoinTournamentButton = ({ 
  tournamentId, 
  tournamentType, 
  entryFee = 0,
  entryFeeType = 'wallet', // 'wallet' (USD) or 'coins' (Golf Coins)
  tournamentName = 'Tournament',
  onJoined,
  disabled = false,
  className = ''
}) => {
  const navigate = useNavigate();
  const [joining, setJoining] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [userBalance, setUserBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Fetch user's balance when dialog opens
  const fetchBalance = async () => {
    setLoadingBalance(true);
    try {
      if (entryFeeType === 'coins') {
        const res = await axios.get(`${API}/golf-coins/balance`);
        setUserBalance({
          type: 'coins',
          amount: res.data.balance || 0
        });
      } else {
        const res = await axios.get(`${API}/wallet/status`);
        setUserBalance({
          type: 'wallet',
          amount: res.data.balance || 0
        });
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
      setUserBalance({ type: entryFeeType, amount: 0 });
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleOpenConfirm = async () => {
    await fetchBalance();
    setShowConfirmDialog(true);
  };

  const handleJoin = async () => {
    try {
      setJoining(true);
      setShowConfirmDialog(false);
      
      let endpoint;
      if (tournamentType === 'skins') {
        endpoint = `${API}/skins/events/${tournamentId}/join`;
      } else if (tournamentType === 'match_play') {
        endpoint = `${API}/match-play/events/${tournamentId}/join`;
      } else {
        endpoint = `${API}/stroke-play/events/${tournamentId}/join`;
      }
      
      const res = await axios.post(endpoint);
      
      if (res.data.success) {
        if (res.data.already_joined) {
          toast.info('You already joined this tournament');
        } else {
          const paymentMsg = entryFeeType === 'coins' 
            ? ` (${entryFee.toLocaleString()} Golf Coins deducted)`
            : res.data.payment 
              ? ` (${res.data.payment.payment_method === 'balance' ? 'Paid from Golf Wallet' : 'Card charged'})`
              : '';
          toast.success(`Joined ${tournamentName}!${paymentMsg}`);
          
          // Dispatch event to refresh Golf Coins balance in header
          window.dispatchEvent(new Event('golfCoinsUpdated'));
        }
        if (onJoined) onJoined(res.data);
      } else {
        // Handle specific errors
        if (res.data.error === 'insufficient_coins') {
          toast.error(res.data.message || 'Not enough Golf Coins');
          // Offer to purchase coins
          setTimeout(() => {
            if (window.confirm('Would you like to purchase Golf Coins?')) {
              navigate('/golf-coins');
            }
          }, 500);
        } else if (res.data.error === 'rules_not_accepted') {
          toast.error('Please accept tournament rules first');
          navigate('/tournament-onboarding');
        } else if (res.data.error === 'age_not_verified') {
          toast.error('Age verification required for cash tournaments');
          navigate('/tournament-onboarding?step=age');
        } else if (res.data.error === 'payout_not_setup') {
          toast.error('Please set up your payout account first');
          navigate('/wallet');
        } else if (res.data.error === 'no_payment_method') {
          toast.error('Please add funds to your Golf Wallet first');
          navigate('/wallet?add_funds=true');
        } else if (res.data.error === 'insufficient_balance') {
          toast.error(res.data.message || 'Insufficient Golf Wallet balance');
          navigate('/wallet?add_funds=true');
        } else if (res.data.error === 'card_declined') {
          toast.error(res.data.message || 'Card declined');
        } else if (res.data.error === 'daily_limit_exceeded') {
          toast.error(res.data.message || 'Daily spending limit reached');
        } else if (res.data.error === 'underage') {
          toast.info(res.data.message || 'You can still enjoy Golf Coins for merchandise!');
        } else {
          toast.error(res.data.message || 'Failed to join tournament');
        }
      }
    } catch (error) {
      console.error('Error joining tournament:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message;
      
      // Handle insufficient coins error from exception
      if (errorMsg?.includes('coins') || errorMsg?.includes('Coins')) {
        toast.error(errorMsg);
        setTimeout(() => {
          if (window.confirm('Would you like to purchase Golf Coins?')) {
            navigate('/golf-coins');
          }
        }, 500);
      } else {
        toast.error(errorMsg || 'Failed to join tournament');
      }
    } finally {
      setJoining(false);
    }
  };

  const isCoins = entryFeeType === 'coins';
  const hasEnoughBalance = userBalance && userBalance.amount >= entryFee;

  return (
    <>
      <Button
        onClick={handleOpenConfirm}
        disabled={joining || disabled}
        className={`${isCoins ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'} text-white ${className}`}
        data-testid="join-tournament-btn"
      >
        {joining ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : isCoins ? (
          <Coins className="w-4 h-4 mr-2" />
        ) : (
          <Wallet className="w-4 h-4 mr-2" />
        )}
        {entryFee > 0 
          ? isCoins 
            ? `Join - ${entryFee.toLocaleString()} Coins`
            : `Join - $${entryFee.toFixed(2)}`
          : 'Join Tournament'
        }
      </Button>

      {/* Confirmation Dialog with Entry Fee Explanation */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md bg-[var(--ps-card-bg,#1a1a1a)] border-[var(--ps-border,#333)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[var(--ps-text-primary,#fff)]">
              <Trophy className="w-5 h-5 text-green-500" />
              Join {tournamentName}
            </DialogTitle>
            <DialogDescription className="text-[var(--ps-text-muted,#888)]">
              Confirm your entry into this tournament
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Entry Fee Type Explanation */}
            <div className={`p-4 rounded-xl border ${isCoins ? 'bg-amber-500/10 border-amber-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
              <div className="flex items-center gap-3 mb-3">
                {isCoins ? (
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <Coins className="w-6 h-6 text-amber-500" />
                  </div>
                ) : (
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <Wallet className="w-6 h-6 text-green-500" />
                  </div>
                )}
                <div>
                  <h4 className={`font-bold ${isCoins ? 'text-amber-400' : 'text-green-400'}`}>
                    {isCoins ? 'Golf Coins Entry' : 'Golf Wallet Entry'}
                  </h4>
                  <p className="text-sm text-[var(--ps-text-muted,#888)]">
                    {isCoins 
                      ? 'Virtual currency for in-app rewards'
                      : 'Real USD for cash prizes'
                    }
                  </p>
                </div>
              </div>

              {/* Entry Fee Amount */}
              <div className="flex justify-between items-center py-2 border-t border-white/10">
                <span className="text-[var(--ps-text-secondary,#aaa)]">Entry Fee</span>
                <span className={`text-xl font-bold ${isCoins ? 'text-amber-400' : 'text-green-400'}`}>
                  {isCoins ? `${entryFee.toLocaleString()} Coins` : `$${entryFee.toFixed(2)}`}
                </span>
              </div>

              {/* User Balance */}
              {loadingBalance ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[var(--ps-text-muted)]" />
                  <span className="text-sm text-[var(--ps-text-muted)]">Checking balance...</span>
                </div>
              ) : userBalance && (
                <div className="flex justify-between items-center py-2 border-t border-white/10">
                  <span className="text-[var(--ps-text-secondary,#aaa)]">Your Balance</span>
                  <span className={`font-semibold ${hasEnoughBalance ? 'text-green-400' : 'text-red-400'}`}>
                    {isCoins 
                      ? `${userBalance.amount.toLocaleString()} Coins`
                      : `$${userBalance.amount.toFixed(2)}`
                    }
                  </span>
                </div>
              )}

              {/* Insufficient Balance Warning */}
              {userBalance && !hasEnoughBalance && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-400 font-medium text-sm">Insufficient Balance</p>
                      <p className="text-red-300/80 text-xs mt-1">
                        {isCoins 
                          ? `You need ${(entryFee - userBalance.amount).toLocaleString()} more coins.`
                          : `You need $${(entryFee - userBalance.amount).toFixed(2)} more.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* How It Works Section */}
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-[var(--ps-text-primary,#fff)]">
                  How {isCoins ? 'Golf Coins' : 'Golf Wallet'} Works
                </span>
              </div>
              <p className="text-xs text-[var(--ps-text-muted,#888)] leading-relaxed">
                {isCoins ? (
                  <>
                    <strong>Golf Coins</strong> are virtual currency you can earn through gameplay or purchase. 
                    Use them for tournaments and redeem for merchandise. 
                    <span className="text-amber-400"> Coins cannot be converted to cash.</span>
                  </>
                ) : (
                  <>
                    <strong>Golf Wallet</strong> holds real USD. Add funds via credit card or bank transfer. 
                    Win tournaments and <span className="text-green-400">withdraw winnings directly to your bank account.</span>
                  </>
                )}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 border-[var(--ps-border,#333)] text-[var(--ps-text-secondary,#aaa)]"
              >
                Cancel
              </Button>
              
              {userBalance && !hasEnoughBalance ? (
                <Button
                  onClick={() => {
                    setShowConfirmDialog(false);
                    navigate(isCoins ? '/golf-coins' : '/wallet?add_funds=true');
                  }}
                  className={`flex-1 ${isCoins ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {isCoins ? 'Get Coins' : 'Add Funds'}
                </Button>
              ) : (
                <Button
                  onClick={handleJoin}
                  disabled={joining || (userBalance && !hasEnoughBalance)}
                  className={`flex-1 ${isCoins ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}
                  data-testid="confirm-join-btn"
                >
                  {joining ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Confirm & Join
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * TournamentPrizeInfo - Shows prize pool info for a tournament
 * Now supports both Golf Wallet (USD) and Golf Coins
 */
const TournamentPrizeInfo = ({ 
  entryFee = 0, 
  entryFeeType = 'wallet',
  playerCount = 0,
  platformFeePercent = 10,
  compact = false
}) => {
  if (!entryFee || entryFee <= 0) return null;
  
  const isCoins = entryFeeType === 'coins';
  const grossPool = entryFee * playerCount;
  const platformFee = grossPool * (platformFeePercent / 100);
  const netPool = grossPool - platformFee;

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm">
        {isCoins ? (
          <Coins className="w-4 h-4 text-amber-500" />
        ) : (
          <Trophy className="w-4 h-4 text-green-500" />
        )}
        <span className="text-[var(--ps-text-muted)]">Prize Pool:</span>
        <span className={`font-semibold ${isCoins ? 'text-amber-500' : 'text-green-500'}`}>
          {isCoins ? `${netPool.toLocaleString()} Coins` : `$${netPool.toFixed(2)}`}
        </span>
      </div>
    );
  }

  return (
    <div className={`rounded-xl p-4 border ${isCoins ? 'bg-gradient-to-r from-amber-600/10 to-yellow-600/10 border-amber-500/20' : 'bg-gradient-to-r from-green-600/10 to-emerald-600/10 border-green-500/20'}`}>
      <div className="flex items-center gap-2 mb-3">
        {isCoins ? (
          <Coins className="w-5 h-5 text-amber-500" />
        ) : (
          <Trophy className="w-5 h-5 text-green-500" />
        )}
        <span className="font-semibold text-[var(--ps-text-primary)]">
          Prize Pool {isCoins && <span className="text-amber-400 text-xs">(Golf Coins)</span>}
        </span>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-[var(--ps-text-muted)]">Entry Fee</span>
          <span className="text-[var(--ps-text-primary)]">
            {isCoins ? entryFee.toLocaleString() : `$${entryFee.toFixed(2)}`} × {playerCount}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--ps-text-muted)]">Gross Pool</span>
          <span className="text-[var(--ps-text-primary)]">
            {isCoins ? grossPool.toLocaleString() : `$${grossPool.toFixed(2)}`}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--ps-text-muted)]">Platform Fee ({platformFeePercent}%)</span>
          <span className="text-red-400">
            -{isCoins ? platformFee.toLocaleString() : `$${platformFee.toFixed(2)}`}
          </span>
        </div>
        <div className="border-t border-[var(--ps-border)] my-2"></div>
        <div className="flex justify-between">
          <span className="font-semibold text-[var(--ps-text-primary)]">Prize Pool</span>
          <span className={`font-bold text-lg ${isCoins ? 'text-amber-500' : 'text-green-500'}`}>
            {isCoins ? `${netPool.toLocaleString()} Coins` : `$${netPool.toFixed(2)}`}
          </span>
        </div>
      </div>
    </div>
  );
};

export { JoinTournamentButton, TournamentPrizeInfo };
export default JoinTournamentButton;
